<template>
  <AcaSchoolPageSkeleton v-if="props.loading" />

  <template v-else-if="props.programData">
    <!-- Contenu détail programme -->
    <!-- ── HERO BANNER avec IMAGE + LOGO ─────────────────────────────────────────────── -->
    <PrepeersHeroCover
      :src="programData?.programdatas.coverPict"
      alt="Écoles PrePeers">
      <div class="hero-banner__logo">
        <PrepeersEntityLogo
          :logo-pict="programData?.programdatas.logoPict"
          :entity-name="programData?.programdatas.name ?? programData?.programdatas.h1 ?? ''"
          :alt="`${programData?.programdatas.name ?? 'Programme'} logo`" />
      </div>
    </PrepeersHeroCover>

    <PrepeersBreadcrumb
      auto
      section="ecoles"
      :current-label="programData?.programdatas.h1" />

    <!-- ── HERO + H1 ─────────────────────────────────────────────── -->
    <section class="hero-section">
      <div class="container">
        <h1 class="hero-section__h1">{{ programData?.programdatas.h1 }}</h1>
        <div
          class="hero-section__intro"
          v-html="formatSeoIntro(programData?.programdatas.introduction ?? '')" />
        <div class="hero-section__line" />
      </div>
    </section>

    <!-- ── LIENS PAGE PARENT ─────────────────────────────────────────────── -->
    <section
      v-if="programData?.parsedSchoolLinkBlock"
      class="level-nav-section-parent">
      <div class="container">
        <div class="level-nav__list">
          <h2>
            <NuxtLink
              v-for="sl in programData?.parsedSchoolLinkBlock.Links"
              :key="sl.Url"
              :to="localePath(sl.Url)"
              class="level-nav__link-parent"
              >{{ sl.Text }}</NuxtLink
            >
          </h2>
        </div>
      </div>
    </section>

    <!-- ── LIENS PAGE TYPE ECOLE ─────────────────────────────────────────────── -->
    <section
      v-if="programData?.parsedParentBlock"
      class="level-nav-section">
      <div
        v-if="programData?.parsedSchoolTypeBlock"
        class="hero-section__subcontainer">
        <h2
          class="hero-section__title"
          v-html="programData?.parsedSchoolTypeBlock.Title"></h2>
        <div
          v-if="filterSeoLinksWithLabel(programData?.parsedSchoolTypeBlock.Links).length"
          class="hero-section__nav">
          <NuxtLink
            v-for="sl in filterSeoLinksWithLabel(programData?.parsedSchoolTypeBlock.Links)"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── BIO STATUS ECOLE ─────────────────────────────────────────────── -->
    <section
      v-if="programData?.programdatas.schoolBioStatus"
      class="level-nav-section-biostatus">
      <div class="container">
        <div
          class="hero-section__intro"
          v-html="formatSeoIntro(programData?.programdatas.schoolBioStatus ?? '')" />
        <div class="hero-section__line" />
      </div>
    </section>

    <!-- ── LIENS PAGE PARENT ─────────────────────────────────────────────── -->
    <section
      v-if="programData?.parsedParentBlock"
      class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ programData?.parsedParentBlock.Title }}</h2>
        <div class="level-nav__list">
          <NuxtLink
            v-for="sl in programData?.parsedParentBlock.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── CTA SI NON CONNECTÉ ─────────────────────────────────────────────── -->
    <section
      v-if="!isLoggedIn"
      class="cta-section">
      <div class="container">
        <PrePeersLolaCta
          variant="programme"
          :entity-name="programData?.programdatas.name ?? programData?.programdatas.h1 ?? ''" />
      </div>
    </section>

    <!-- ── LIENS PAGE PAYS ─────────────────────────────────────────────── -->
    <section
      v-if="programData?.parsedCountriesBlock?.Links?.length"
      class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ programData?.parsedCountriesBlock.Title }}</h2>
        <div class="level-nav__list">
          <NuxtLink
            v-for="sl in programData?.parsedCountriesBlock.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── LIENS PAGE VILLES ─────────────────────────────────────────────── -->
    <section
      v-if="programData?.parsedCitiesBlock?.Links?.length"
      class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ programData?.parsedCitiesBlock.Title }}</h2>
        <div class="level-nav__list">
          <NuxtLink
            v-for="sl in programData?.parsedCitiesBlock.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── DESCRIPTION ─────────────────────────────────────────────── -->
    <section
      v-if="programData?.parsedProgramDescriptionBlock"
      class="specific-section content-section surface-panel">
      <div class="container">
        <h2 class="content-section__title">
          {{ programData?.parsedProgramDescriptionBlock.Title }}
        </h2>
        <div
          v-if="programData?.parsedProgramDescriptionBlock.DegreeBlock?.Links?.length"
          class="specific-section__subsection">
          <h3 class="content-section__subtitle">
            {{ programData?.parsedProgramDescriptionBlock.DegreeBlock.Title }}
          </h3>
          <div class="level-nav__list">
            <NuxtLink
              v-for="sl in programData?.parsedProgramDescriptionBlock.DegreeBlock.Links"
              :key="sl.Url"
              :to="localePath(sl.Url)"
              class="level-nav__link"
              >{{ sl.Text }}</NuxtLink
            >
          </div>
        </div>
        <div
          v-if="programData?.parsedProgramDescriptionBlock.FormatStyle?.Items?.length"
          class="content-section__subsection">
          <h3 class="content-section__subtitle">
            {{ programData?.parsedProgramDescriptionBlock.FormatStyle.Title }}
          </h3>
          <ul class="content-section__list">
            <li
              v-for="(item, index) in programData?.parsedProgramDescriptionBlock.FormatStyle.Items"
              :key="index"
              class="content-section__list-item">
              {{ item }}
            </li>
          </ul>
        </div>
        <div
          v-if="programData?.parsedProgramDescriptionBlock.Duration?.Items?.length"
          class="content-section__subsection">
          <h3 class="content-section__subtitle">
            {{ programData?.parsedProgramDescriptionBlock.Duration.Title }}
          </h3>
          <ul class="content-section__list">
            <li
              v-for="(item, index) in programData?.parsedProgramDescriptionBlock.Duration.Items"
              :key="index"
              class="content-section__list-item">
              {{ item }}
            </li>
          </ul>
        </div>
        <div
          v-if="programData?.parsedProgramDescriptionBlock.Recognition?.Items?.length"
          class="content-section__subsection">
          <h3 class="content-section__subtitle">
            {{ programData?.parsedProgramDescriptionBlock.Recognition.Title }}
          </h3>
          <ul class="content-section__list">
            <li
              v-for="(item, index) in programData?.parsedProgramDescriptionBlock.Recognition.Items"
              :key="index"
              class="content-section__list-item">
              {{ item }}
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- ── DESCRIPTION WHAT ─────────────────────────────────────────────── -->
    <section
      v-if="programData?.parsedProgramDescrWhatBlock"
      class="specific-section content-section surface-panel">
      <div class="container">
        <h2 class="content-section__title">{{ programData?.parsedProgramDescrWhatBlock.Title }}</h2>
        <UiPrepeersQlEditorContent :html="programData?.parsedProgramDescrWhatBlock.Content ?? ''" />
      </div>
    </section>

    <!-- ── DESCRIPTION WHY ─────────────────────────────────────────────── -->
    <section
      v-if="programData?.parsedProgramDescrWhyBlock"
      class="specific-section content-section surface-panel">
      <div class="container">
        <h2 class="content-section__title">{{ programData?.parsedProgramDescrWhyBlock.Title }}</h2>
        <UiPrepeersQlEditorContent :html="programData?.parsedProgramDescrWhyBlock.Content ?? ''" />
      </div>
    </section>

    <!-- ── DESCRIPTION CONDITIONS ADMISSION ─────────────────────────────────────────────── -->
    <section
      v-if="programData?.parsedProgramAdmissionBlock"
      class="specific-section content-section surface-panel">
      <div class="container">
        <h2 class="content-section__title">{{ programData?.parsedProgramAdmissionBlock.Title }}</h2>
        <UiPrepeersQlEditorContent :html="programData?.parsedProgramAdmissionBlock.Content ?? ''" />
      </div>
    </section>

    <!-- ── NOS CHIFFRES POST FORMATION ─────────────────────────────────────────────── -->
    <section
      v-if="programData?.parsedStatsBlock"
      class="stats-section surface-panel surface-panel--elevated">
      <div class="container">
        <h2 class="stats__title">{{ programData?.parsedStatsBlock.Employment.Title }}</h2>
        <p class="stats__subtitle">{{ programData?.parsedStatsBlock.Employment.SubTitle }}</p>

        <!-- DONUT + SALAIRES -->
        <div class="stats__top-row">
          <!-- DONUT -->
          <div class="stats__donut-wrapper">
            <svg
              viewBox="0 0 42 42"
              class="stats__donut"
              :class="{ 'stats__donut--centered': !hasSalaryData }">
              <defs>
                <linearGradient
                  id="linear-gradient-donut"
                  x1="1"
                  x2="0"
                  y2="1"
                  gradientUnits="objectBoundingBox">
                  <stop
                    offset="0"
                    stop-color="#ffc300" />
                  <stop
                    offset="1"
                    stop-color="#ff5100" />
                </linearGradient>
              </defs>
              <!-- Fond gris -->
              <circle
                cx="21"
                cy="21"
                r="15.91549430918954"
                fill="transparent"
                stroke="#5a4a3a"
                stroke-width="6" />
              <!-- Segment orange -->
              <circle
                cx="21"
                cy="21"
                r="15.91549430918954"
                fill="transparent"
                stroke="url(#linear-gradient-donut)"
                stroke-width="7"
                :stroke-dasharray="`${programData?.parsedStatsBlock.Employment.EmploymentRate} ${100 - programData?.parsedStatsBlock.Employment.EmploymentRate}`"
                stroke-dashoffset="0"
                stroke-linecap="round" />
              <!-- Trou central -->
              <circle
                cx="21"
                cy="21"
                r="10.91549430918954"
                fill="#1a1a2e" />
            </svg>
            <div class="stats__donut-text">
              <div class="stats__donut-value">
                {{ programData?.parsedStatsBlock.Employment.EmploymentRate }}%
              </div>
              <div class="stats__donut-label">Taux net d'emploi</div>
            </div>
          </div>

          <!-- SALAIRES -->
          <div
            v-if="hasSalaryData"
            class="stats__salaries">
            <h3 class="stats__salaries-title">Les rémunérations moyennes</h3>

            <!-- FRANCE -->
            <div class="stats__salary-country">
              <h4 class="stats__salary-country-title">France</h4>

              <div class="stats__salary-item">
                <div class="stats__salary-label">Salaire annuel brut hors primes :</div>
                <div class="stats__salary-value">
                  Entre {{ programData?.parsedStatsBlock.Employment.SalMinFR }} et
                  {{ programData?.parsedStatsBlock.Employment.SalMaxFR }}
                </div>
              </div>

              <div class="stats__salary-item">
                <div class="stats__salary-label">
                  Salaire annuel brut avec primes et avantages :
                </div>
                <div class="stats__salary-value">
                  Entre {{ programData?.parsedStatsBlock.Employment.SalMinWithPFR }} et
                  {{ programData?.parsedStatsBlock.Employment.SalMaxWithPFR }}
                </div>
              </div>
            </div>

            <!-- ÉTRANGER -->
            <div class="stats__salary-country">
              <h4 class="stats__salary-country-title">Étranger</h4>

              <div class="stats__salary-item">
                <div class="stats__salary-label">Salaire annuel brut hors primes :</div>
                <div class="stats__salary-value">
                  Entre {{ programData?.parsedStatsBlock.Employment.SalMinAB }} et
                  {{ programData?.parsedStatsBlock.Employment.SalMaxAB }}
                </div>
              </div>

              <div class="stats__salary-item">
                <div class="stats__salary-label">
                  Salaire annuel brut avec primes et avantages :
                </div>
                <div class="stats__salary-value">
                  Entre {{ programData?.parsedStatsBlock.Employment.SalMinWithPAB }} et
                  {{ programData?.parsedStatsBlock.Employment.SalMaxWithPAB }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-for="stat in programData?.parsedStatsBlock.Stats"
          :key="stat.Title">
          <div
            v-if="stat.Links.length > 0"
            class="stats__bottom-section">
            <h3 class="stats__title">{{ stat.Title }}</h3>
            <div class="stats__wrapper">
              <NuxtLink
                v-for="link in stat.Links"
                :key="link.Url"
                :to="link.Url"
                class="stats__tag">
                {{ link.Text }}
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ──  MAP ─────────────────────────────────────────────── -->
    <AcaAddressesMapAndList :campus-block="programData?.parsedAddressesBlock ?? null" />
  </template>
</template>

<script setup lang="ts">
import AcaSchoolPageSkeleton from '~/components/aca/AcaSchoolPageSkeleton.vue'
import PrepeersEntityLogo from '~/components/core/PrepeersEntityLogo.vue'
import PrepeersHeroCover from '~/components/core/PrepeersHeroCover.vue'
import PrepeersBreadcrumb from '~/components/navigation/PrepeersBreadcrumb.vue'
import type { ProgramDataResponse } from '~/types/school'
import { filterSeoLinksWithLabel, formatSeoIntro } from '~/utils/helpers'
import PrePeersLolaCta from '~/components/ui/PrePeersLolaCta.vue'

interface ProgramDetailProps {
  programData: ProgramDataResponse | null
  loading?: boolean
  isLoggedIn: boolean
}

const props = withDefaults(defineProps<ProgramDetailProps>(), {
  loading: false,
})

const localePath = useLocalePath()
useI18n()

const hasSalaryData = computed(() => {
  if (!props.programData?.parsedStatsBlock) return false

  const salaries = props.programData.parsedStatsBlock

  // Vérifie si au moins une donnée de salaire est renseignée
  const hasFrenchSalary = salaries.Employment.SalMinFR && salaries.Employment.SalMaxFR
  const hasAbroadSalary = salaries.Employment.SalMinAB && salaries.Employment.SalMaxAB

  return !!(hasFrenchSalary || hasAbroadSalary)
})

console.log('ProgramDetail props:', props)
</script>

<style scoped>
.level-nav-section-parent {
  padding-top: 0px;
}

.level-nav__link-parent {
  padding: var(--space-3) var(--space-6);
  background-color: transparent;
  border: none;
  color: var(--text-secondary);
  font-size: var(--text-2xl);
  font-weight: 600;
  transition: all var(--duration-fast) var(--ease-out);
}

.level-nav__link-parent:hover {
  color: var(--accent-blue-lt);
}

.level-nav-section-biostatus {
  text-align: center;
}

/* ── SPECIFIC BLOCKS ─────────────────────────────────────────────── */
.specific-section {
  margin-bottom: var(--space-8);
}

.specific__title {
  font-size: var(--text-2xl);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-12);
  text-align: left;
}

.specific__value {
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  color: white;
  line-height: 1;
  margin-bottom: var(--space-3);
  letter-spacing: var(--tracking-tight);
}

.specific__label {
  font-size: var(--text-sm);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  line-height: var(--leading-tight);
}

.content-section__subtitle {
  color: var(--text-primary);
  margin-bottom: var(--space-6);
  margin-top: var(--space-12);
}

.content-section__list {
  list-style: disc;
  padding-left: var(--space-8);
}

/* ── NOS CHIFFRES POST FORMATION ─────────────────────────────────────────────── */
.stats__title {
  font-size: var(--text-2xl);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-2);
  text-align: left;
}

.stats__subtitle {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin-bottom: var(--space-12);
  font-style: italic;
}

/* ── DONUT + SALAIRES ─────────────────────────────────────────────── */
.stats__top-row {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: var(--space-12);
  margin-bottom: var(--space-1);
  align-items: start;
}

.stats__donut-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-8);
  background-color: var(--bg-elevated);
  border-radius: var(--radius-lg);
  text-align: center;
}

.stats__donut {
  width: 100%;
  max-width: 280px;
  height: auto;
  aspect-ratio: 1;
  margin-bottom: var(--space-8);
}

.stats__donut--centered {
  max-width: 320px;
}

.stats__donut-text {
  width: 100%;
}

.stats__donut-value {
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 800;
  color: white;
  line-height: 1;
  margin-bottom: var(--space-2);
  letter-spacing: var(--tracking-tight);
}

.stats__donut-label {
  font-size: var(--text-sm);
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

/* ── SALAIRES ─────────────────────────────────────────────── */
.stats__salaries {
  background-color: rgba(0, 0, 0, 0.2);
  padding: var(--space-8);
  border-radius: var(--radius-lg);
}

.stats__salaries-title {
  font-size: var(--text-lg);
  font-weight: 700;
  color: #ffc300;
  margin-bottom: var(--space-8);
  text-align: left;
}

.stats__salary-country {
  margin-bottom: var(--space-8);
}

.stats__salary-country:last-child {
  margin-bottom: 0;
}

.stats__salary-country-title {
  font-size: var(--text-md);
  font-weight: 700;
  color: #ffc300;
  margin-bottom: var(--space-4);
  text-align: left;
}

.stats__salary-item {
  margin-bottom: var(--space-4);
}

.stats__salary-item:last-child {
  margin-bottom: 0;
}

.stats__salary-label {
  font-size: var(--text-sm);
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  margin-bottom: var(--space-1);
}

.stats__salary-value {
  font-size: var(--text-sm);
  color: rgba(255, 255, 255, 0.8);
  padding-left: var(--space-4);
  border-left: 2px solid rgba(255, 195, 0, 0.3);
}

/* ── SECTEURS PROFESSIONNELS ─────────────────────────────────────────────── */
.stats__bottom-section {
  padding-top: var(--space-8);
  /* border-top: 1px solid rgba(255, 255, 255, 0.1); */
}

.stats__title {
  font-size: var(--text-xl);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-8);
  text-align: left;
}

.stats__wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  justify-content: center;
}

.stats__group {
  display: contents;
}

.stats__tag {
  display: inline-block;
  padding: var(--space-3) var(--space-6);
  background-color: rgba(0, 0, 0, 0.3);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-md);
  text-decoration: none;
  font-size: var(--text-sm);
  font-weight: 500;
  transition: all var(--duration-fast) var(--ease-out);
}

.stats__:hover {
  background-color: rgba(255, 195, 0, 0.2);
  border-color: rgba(255, 195, 0, 0.5);
  color: #ffc300;
}

/* ── LEAFLET OVERRIDES ─────────────────────────────────────────────── */
:deep(.leaflet-control-attribution) {
  background-color: rgba(255, 255, 255, 0.8) !important;
  font-size: 11px !important;
}

:deep(.leaflet-marker-icon) {
  filter: hue-rotate(0deg) brightness(1.1);
}

/* ── RESPONSIVE ─────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .metrics__grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-5);
  }

  .metrics__card {
    min-height: 130px;
    padding: var(--space-6);
  }

  .metrics__value {
    font-size: clamp(1.75rem, 4vw, 2.5rem);
  }

  .stats__top-row {
    grid-template-columns: 1fr;
    gap: var(--space-8);
  }

  .stats__salaries {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-6);
  }

  .stats__salaries-title {
    grid-column: 1 / -1;
  }

  .stats__salary-country {
    background-color: rgba(0, 0, 0, 0.3);
    padding: var(--space-6);
    border-radius: var(--radius-md);
    margin-bottom: 0;
  }
}

@media (max-width: 768px) {
  .metrics-section {
    padding: var(--space-16) var(--container-pad);
  }

  .metrics__title {
    font-size: var(--text-xl);
    margin-bottom: var(--space-8);
  }

  .metrics__grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-4);
  }

  .metrics__card {
    min-height: 120px;
    padding: var(--space-5);
  }

  .metrics__value {
    font-size: clamp(1.5rem, 3vw, 2rem);
  }

  .metrics__label {
    font-size: var(--text-xs);
  }

  .director__title {
    font-size: var(--text-xl);
    margin-bottom: var(--space-8);
  }

  .director__content {
    gap: var(--space-8);
  }

  .director__text {
    font-size: var(--text-sm);
    max-width: 600px;
  }

  .director__photo-wrapper {
    width: 120px;
    height: 120px;
  }

  .director__name {
    font-size: var(--text-lg);
  }

  .director__linkedin {
    width: 40px;
    height: 40px;
  }

  .director__linkedin svg {
    width: 20px;
    height: 20px;
  }

  .stats__title {
    font-size: var(--text-xl);
  }

  .stats__top-row {
    gap: var(--space-6);
    margin-bottom: var(--space-12);
  }

  .stats__donut {
    max-width: 240px;
  }

  .stats__donut-value {
    font-size: var(--text-4xl);
  }

  .stats__donut-label {
    font-size: var(--text-xs);
  }

  .stats__salaries {
    display: block;
  }

  .stats__salary-country {
    margin-bottom: var(--space-6);
  }

  .stats__salary-country:last-child {
    margin-bottom: 0;
  }

  .stats__salary-country-title {
    font-size: var(--text-base);
  }

  .stats__salary-label {
    font-size: var(--text-xs);
  }

  .stats__salary-value {
    font-size: var(--text-xs);
  }

  .stats__sectors-title {
    font-size: var(--text-lg);
  }

  .stats__sector-tag {
    font-size: var(--text-xs);
    padding: var(--space-2) var(--space-4);
  }
}

@media (max-width: 640px) {
  .metrics__title {
    font-size: var(--text-lg);
    margin-bottom: var(--space-6);
  }

  .metrics__grid {
    grid-template-columns: 1fr;
    gap: var(--space-3);
  }

  .metrics__card {
    min-height: 100px;
    padding: var(--space-4);
  }

  .metrics__value {
    font-size: var(--text-3xl);
  }

  .metrics__label {
    font-size: var(--text-xs);
  }

  .director__title {
    font-size: var(--text-lg);
    margin-bottom: var(--space-6);
  }

  .director__content {
    gap: var(--space-6);
  }

  .director__text {
    font-size: var(--text-sm);
    line-height: var(--leading-normal);
  }

  .director__photo-wrapper {
    width: 100px;
    height: 100px;
    border-width: 2px;
  }

  .director__name {
    font-size: var(--text-base);
  }

  .director__linkedin {
    width: 36px;
    height: 36px;
  }

  .director__linkedin svg {
    width: 18px;
    height: 18px;
  }

  .stats__title {
    font-size: var(--text-lg);
  }

  .stats__subtitle {
    font-size: var(--text-xs);
  }

  .stats__donut {
    max-width: 200px;
  }

  .stats__donut-value {
    font-size: var(--text-3xl);
  }

  .stats__salaries {
    padding: var(--space-6);
  }

  .stats__salary-country {
    margin-bottom: var(--space-4);
  }

  .stats__salary-label {
    font-size: var(--text-xs);
  }

  .stats__salary-value {
    font-size: var(--text-xs);
  }

  .stats__sectors-title {
    font-size: var(--text-base);
  }

  .stats__sector-tag {
    font-size: var(--text-xs);
  }

  .stats__bottom-section {
    margin-top: var(--space-8);
    padding-top: var(--space-6);
  }
}

/* ── Mobile (≤640px) ──────────────────────────────────────── */
@media (max-width: 640px) {
  .level-nav__link-parent {
    padding: var(--space-2) var(--space-4);
    font-size: var(--text-xs);
  }
}
</style>
